import gsap from 'gsap';
import { getCurrentDevice } from '../utils';

export class Navigation {
	constructor( selector, options = {} ) {
		this.container = document.querySelector( selector );
		this.options = options;
		this.body = document.querySelector( 'body' );
		this.mask = null;
		this.panel = null;
		this.toggle = null;
		this.type = options.type || 'fade';
		this.hasToggle = false;
		this.flag = false;
		this.currentDevice = getCurrentDevice();
		this.clickFunc = null;
		this.clickPanelFunc = null;
		this.clickOutsideFunc = null;
		this.timer = null;
		this.init();
	}

	init() {
		if ( ! this.container ) return;
		window.addEventListener( 'DOMContentLoaded', () => {
			this.execute();
		} );
		this.resize();
	}

	execute() {
		const { devices } = this.options;
		this.panel = this.container?.querySelector( '.el-panel' );
		this.toggle = this.container?.querySelector( '.el-toggle' );
		if ( devices.includes( this.currentDevice ) ) {
			this.setHasToggle();
		} else {
			this.setNoToggle();
		}
	}

	destroy() {
		if ( this.hasToggle )
			this.toggle.removeEventListener( 'click', this.clickFunc );
		this.container.classList.remove( 'is-expanded' );
		this.body.classList.remove( 'modal-open' );
		this.panel.removeAttribute( 'aria-expanded' );
		this.toggle.removeAttribute( 'aria-expanded' );
		this.flag = false;
		this.hasToggle = false;
	}

	setHasToggle() {
		// Reset
		const iconOpen = gsap.utils.toArray( '.icon-open', this.toggle );
		const iconClose = gsap.utils.toArray( '.icon-close', this.toggle );
		const textOpen = gsap.utils.toArray( '.text-open', this.toggle );
		const textClose = gsap.utils.toArray( '.text-close', this.toggle );
		let clearArray = [ this.toggle, this.panel ];
		clearArray = clearArray.concat(
			iconOpen,
			iconClose,
			textOpen,
			textClose
		);
		gsap.set( clearArray, { clearProps: 'all' } );
		this.panel.setAttribute( 'aria-expanded', 'false' );
		this.toggle.setAttribute( 'aria-expanded', 'false' );

		// Initial display
		const panelDisplay =
			this.options.panelDisplay?.[ this.currentDevice ] || 'flex';
		const toggleDisplay =
			this.options.toggleDisplay?.[ this.currentDevice ] || 'block';
		gsap.set( this.toggle, { display: toggleDisplay } );
		gsap.set( this.panel, { display: 'none' } );

		// Mask
		if ( this.options.isMask ) {
			this.mask = document.createElement( 'div' );
			this.mask.classList.add( 'mk-overlay-mask' );
			this.container.insertBefore( this.mask, this.panel );
		}

		// Toggle animation
		const toggleTween = gsap.timeline( { paused: true } );
		if ( iconOpen && iconClose ) {
			toggleTween
				.fromTo(
					iconOpen,
					{ ratate: 0, opacity: 1 },
					{ ratate: 90, opacity: 0, duration: 0.3 }
				)
				.fromTo(
					iconClose,
					{ rotate: -45, opacity: 0, display: 'none' },
					{
						rotate: 0,
						opacity: 1,
						display: 'flex',
						duration: 0.2,
					},
					'-=0.2'
				);
		}
		if ( textOpen && textClose ) {
			toggleTween
				.fromTo(
					textOpen,
					{ yPercent: 0, opacity: 1 },
					{ yPercent: -100, opacity: 0, duration: 0.3 },
					'<'
				)
				.fromTo(
					textClose,
					{ yPercent: 0, opacity: 0 },
					{ yPercent: -100, opacity: 1, duration: 0.1 },
					'-=0.1'
				);
		}

		const coreTween = gsap.timeline( {
			paused: true,
			onStart: () => this.body.classList.add( 'modal-open' ),
			onComplete: () => {
				this.container.classList.add( 'is-expanded' );
				this.panel.setAttribute( 'aria-expanded', 'true' );
				this.toggle.setAttribute( 'aria-expanded', 'true' );
			},
			onReverseComplete: () => {
				this.container.classList.remove( 'is-expanded' );
				this.body.classList.remove( 'modal-open' );
				this.panel.setAttribute( 'aria-expanded', 'false' );
				this.toggle.setAttribute( 'aria-expanded', 'false' );
			},
		} );
		if ( this.mask )
			coreTween.fromTo(
				this.mask,
				{ display: 'none', opacity: 0 },
				{ display: 'block', opacity: 1, duration: 0.3 }
			);
		if ( this.options.type === 'none' ) {
			coreTween
				.set( this.panel, { display: 'none' }, '<' )
				.set( this.panel, { display: panelDisplay }, '<' );
		} else {
			const fromVars = { display: 'none' };
			const toVars = { display: panelDisplay };
			toVars.duration = this.options.duration || 0.5;
			if ( this.options.delay ) toVars.delay = this.options.delay;
			toVars.ease = this.options.ease || 'power1.inOut';
			if ( this.options.type === 'slide' ) {
				if ( this.options.direction === 'top' ) {
					fromVars.yPercent = 100;
					toVars.yPercent = 0;
					coreTween.set(
						this.panel,
						{ top: 'auto', bottom: 0 },
						'-=0.2'
					);
				} else if ( this.options.direction === 'right' ) {
					fromVars.xPercent = -100;
					toVars.xPercent = 0;
					coreTween.set(
						this.panel,
						{ left: 0, right: 'auto' },
						'-=0.2'
					);
				} else if ( this.options.direction === 'left' ) {
					fromVars.xPercent = 100;
					toVars.xPercent = 0;
					coreTween.set(
						this.panel,
						{ left: 'auto', right: 0 },
						'-=0.2'
					);
				} else {
					fromVars.yPercent = -100;
					toVars.yPercent = 0;
					coreTween.set(
						this.panel,
						{ top: 0, bottom: 'auto' },
						'-=0.2'
					);
				}
				coreTween.fromTo( this.panel, fromVars, toVars );
			} else if ( this.options.type === 'expand' ) {
				if ( this.options.direction === 'top' ) {
					gsap.set( this.panel, {
						display: panelDisplay,
						bottom: 0,
						top: window.innerHeight,
					} );
					const theWrap = this.panel.querySelector( '.panel-wrap' );
					let theWidth = theWrap.offsetWidth;
					if ( theWidth > window.innerWidth )
						theWidth = window.innerWidth;
					let theTop = window.innerHeight - theWrap.offsetHeight;
					if ( theTop < 0 ) theTop = 0;
					fromVars.top = window.innerHeight;
					fromVars.width = theWidth;
					toVars.top = theTop;
					coreTween.set(
						theWrap,
						{ bottom: 0, position: 'absolute' },
						'-=0.2'
					);
				} else if ( this.options.direction === 'right' ) {
					gsap.set( this.panel, {
						display: panelDisplay,
						left: 0,
						right: window.innerWidth,
					} );
					const theWrap = this.panel.querySelector( '.panel-wrap' );
					let theHeight = theWrap.offsetHeight;
					if ( theHeight > window.innerHeight )
						theHeight = window.innerHeight;
					let theRight = window.innerWidth - theWrap.offsetWidth;
					if ( theRight < 0 ) theRight = 0;
					fromVars.right = window.innerWidth;
					fromVars.height = theHeight;
					toVars.right = theRight;
					coreTween.set(
						theWrap,
						{ left: 0, position: 'absolute' },
						'-=0.2'
					);
				} else if ( this.options.direction === 'left' ) {
					gsap.set( this.panel, {
						display: panelDisplay,
						right: 0,
						left: window.innerWidth,
					} );
					const theWrap = this.panel.querySelector( '.panel-wrap' );
					let theHeight = theWrap.offsetHeight;
					if ( theHeight > window.innerHeight )
						theHeight = window.innerHeight;
					let theLeft = window.innerWidth - theWrap.offsetWidth;
					if ( theLeft < 0 ) theLeft = 0;
					fromVars.left = window.innerWidth;
					fromVars.height = theHeight;
					toVars.left = theLeft;
					coreTween.set(
						theWrap,
						{ right: 0, position: 'absolute' },
						'-=0.2'
					);
				} else {
					fromVars.height = 0;
					toVars.height = 'auto';
					coreTween.set(
						this.panel,
						{ top: 0, bottom: 'auto', display: panelDisplay },
						'-=0.2'
					);
				}
				delete fromVars.display;
				delete toVars.display;
				coreTween.fromTo( this.panel, fromVars, toVars );
			} else {
				fromVars.opacity = 0;
				toVars.opacity = 1;
				coreTween.fromTo( this.panel, fromVars, toVars, '-=0.2' );
			}
		}
		const tempClick = ( e ) => this.click( e, coreTween, toggleTween );
		this.clickFunc = tempClick;
		this.toggle.addEventListener( 'click', this.clickFunc );
		this.hasToggle = true;
	}

	setNoToggle() {
		this.body.classList.remove( 'modal-open' );
		const panelDisplay =
			this.options.panelDisplay?.[ this.currentDevice ] || 'flex';
		gsap.set( this.toggle, { clearProps: 'all' } );
		gsap.set( this.toggle, { display: 'none' } );
		gsap.set( this.panel, { clearProps: 'all' } );
		gsap.set( this.panel, { display: panelDisplay, overflow: 'visible' } );
	}

	click( e, coreTween, toggleTween ) {
		e.stopPropagation();
		if ( ! this.flag ) {
			coreTween.play();
			toggleTween.play();
			const tempClickPanel = ( event ) => this.clickPanel( event );
			const tempClickOutside = () =>
				this.clickOutside( coreTween, toggleTween );
			this.clickPanelFunc = tempClickPanel;
			this.clickOutsideFunc = tempClickOutside;
			this.panel.addEventListener( 'click', this.clickPanelFunc );
			document.addEventListener( 'click', this.clickOutsideFunc );
		} else {
			coreTween.reverse();
			toggleTween.reverse();
			this.panel.removeEventListener( 'click', this.clickPanelFunc );
			document.removeEventListener( 'click', this.clickOutsideFunc );
		}
		this.flag = ! this.flag;
	}

	clickPanel = ( e ) => {
		e.stopPropagation();
	};

	clickOutside( coreTween, toggleTween ) {
		coreTween.reverse();
		toggleTween.reverse();
		this.panel.removeEventListener( 'click', this.clickPanelFunc );
		document.removeEventListener( 'click', this.clickOutsideFunc );
		this.flag = ! this.flag;
	}

	resize() {
		window.addEventListener( 'resize', () => {
			this.timer?.kill();
			this.timer = gsap.delayedCall( 0.5, () => {
				const prevDevice = this.currentDevice;
				this.currentDevice = getCurrentDevice();
				if ( prevDevice !== this.currentDevice ) {
					this.destroy();
					this.execute();
				}
			} );
		} );
	}
}
